// src/pages/admin/AdminAuditManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import StatusNotificationBadge from '../../components/ui/StatusNotificationBadge';
import AdminQueueIndicator from '../../components/ui/AdminQueueIndicator';
import PendingAuditsTable from './components/PendingAuditsTable';
import AuditUploadInterface from './components/AuditUploadInterface';
import UserAssignmentPanel from './components/UserAssignmentPanel';
import FilePreviewModal from './components/FilePreviewModal';
import CompletedSubmissionsTable from './components/CompletedSubmissionsTable';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { auditReportService } from '../../services/auditReportService';
import GlobalFooter from '../../components/GlobalFooter';
import { emailService } from '../../services/emailService';

const STORAGE_BUCKET = 'html-reports';
const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 6000];
const DEFAULT_AVATAR = 'https://img.rocket.new/generatedImages/rocket_gen_img_14da91c34-1763294780479.png';

// ─── Error Boundary ───────────────────────────────────────────────────────────

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error('ErrorBoundary:', err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-destructive/20 p-6 max-w-md text-center">
            <Icon name="AlertTriangle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">Please refresh the page or try again later.</p>
            <Button onClick={() => window.location.reload()} variant="default">Refresh Page</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Submission Detail Modal ──────────────────────────────────────────────────

const SubmissionDetailModal = ({ audit, isOpen, onClose }) => {
  if (!isOpen || !audit) return null;

  const fmtFull = (d) => d
    ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'N/A';

  const fields = [
    { icon: 'Hash',      label: 'Request ID',  value: audit.requestId },
    { icon: 'User',      label: 'User Name',   value: audit.userName },
    { icon: 'Mail',      label: 'Email',        value: audit.userEmail },
    { icon: 'Crown',     label: 'Audit Type',  value: audit.auditType },
    { icon: 'Tag',       label: 'Status',       value: audit.status },
    { icon: 'Briefcase', label: 'Industry',     value: audit.industry },
    { icon: 'Target',    label: 'Purpose',      value: audit.purpose },
    { icon: 'Users',     label: 'Audience',     value: audit.audience },
    { icon: 'Layers',    label: 'Slides',       value: audit.slideCount ? `${audit.slideCount} slides` : null },
    { icon: 'FileText',  label: 'File',         value: audit.fileName },
    { icon: 'Clock',     label: 'Submitted',    value: fmtFull(audit.submissionDate) },
    { icon: 'Calendar',  label: 'Registered',   value: fmtFull(audit.registrationDate) },
  ].filter(f => f.value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-foreground">Submission Details</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{audit.requestId || audit.id}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="p-6">
          <div className="mb-5 flex items-center gap-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="Clock" size={16} className="text-warning flex-shrink-0" />
            <p className="text-sm text-warning font-medium">
              Pending — no audit report has been generated yet.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(({ icon, label, value }) => (
              <div key={label} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Icon name={icon} size={12} /> {label}
                </p>
                <p className="text-sm font-medium text-foreground break-all">{value}</p>
              </div>
            ))}
          </div>
          {audit.notes && (
            <div className="mt-4 space-y-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Icon name="StickyNote" size={12} /> Notes
              </p>
              <p className="text-sm text-foreground bg-muted/30 p-3 rounded-lg">{audit.notes}</p>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminAuditManagement = () => {
  const { getClientForRole } = useAuth();
  const adminClient = useMemo(() => getClientForRole('admin'), [getClientForRole]);

  // ── Raw data from DB ──────────────────────────────────────────────────────
  const [auditReports, setAuditReports] = useState([]);
  const [submissions,  setSubmissions]  = useState([]);
  const [htmlFiles,    setHtmlFiles]    = useState([]);
  const [users,        setUsers]        = useState([]);
  const [orders,       setOrders]       = useState([]);

  const [assignedFileIds, setAssignedFileIds] = useState(new Set());
  const assignedFileIdsRef = useRef(new Set());

  const updateAssignedIds = useCallback((updaterFn) => {
    setAssignedFileIds(prev => {
      const next = updaterFn(prev);
      assignedFileIdsRef.current = next;
      return next;
    });
  }, []);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isLoading,      setIsLoading]      = useState(true);
  const [isRefreshing,   setIsRefreshing]   = useState(false);
  const [error,          setError]          = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // ── Modal state ───────────────────────────────────────────────────────────
  const [previewFile, setPreviewFile] = useState(null);
  const [detailAudit, setDetailAudit] = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const fetchTimeoutRef     = useRef(null);
  const abortControllerRef  = useRef(null);
  const refreshAttemptsRef  = useRef({});
  const realtimeChannelsRef = useRef([]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      Object.values(refreshAttemptsRef.current).forEach(clearTimeout);
      realtimeChannelsRef.current.forEach(ch => ch?.unsubscribe?.());
    };
  }, []);

  // ── Real-time subscriptions ───────────────────────────────────────────────
  useEffect(() => {
    if (!adminClient) return;
    const tables = ['audit_reports', 'deck_submissions', 'html_content'];
    const channels = tables.map(table =>
      adminClient
        .channel(`${table}_changes`)
        .on('postgres_changes', { event: '*', schema: 'public', table }, () => {
          if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = setTimeout(fetchData, 500);
        })
        .subscribe()
    );
    realtimeChannelsRef.current = channels;
    return () => channels.forEach(ch => ch?.unsubscribe?.());
  }, [adminClient]);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!adminClient) return;
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      setError(null);

      const [submissionsResult, reportsData, usersData, ordersResult] = await Promise.allSettled([
        adminClient
          .from('deck_submissions')
          .select('id,user_id,title,file_name,file_url,status,submitted_at,created_at,updated_at,slide_count,audit_type,priority,request_id,purpose,audience,industry,notes,payment_order_id')
          .order('submitted_at', { ascending: false }),
        auditReportService.getAllAuditReports(),
        auditReportService.getAllUsers(),
        // ✅ FIX 2: Added created_at to select — needed for failed/cancelled date display
        adminClient
          .from('paid_audit_orders')
          .select('id, submission_id, user_id, payment_status, razorpay_payment_id, amount, paid_at, created_at'),
      ]);

      const subs       = submissionsResult.status === 'fulfilled' ? (submissionsResult.value.data || []) : [];
      const reports    = reportsData.status === 'fulfilled' ? (reportsData.value || []) : [];
      const uList      = usersData.status === 'fulfilled' ? (usersData.value || []) : [];
      const ordersList = ordersResult.status === 'fulfilled' ? (ordersResult.value.data || []) : [];

      setSubmissions(subs);
      setAuditReports(reports);
      setUsers(uList);
      setOrders(ordersList);

      await fetchHtmlFilesWithReports(reports);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('fetchData error:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [adminClient]);

  useEffect(() => { fetchData(); }, []);

  // ── HTML file fetching ────────────────────────────────────────────────────

  const lookupAuditByShortId = useCallback(async (shortId, cache) => {
    if (!shortId) return null;
    if (cache[shortId]) return cache[shortId];
    try {
      const { data, error } = await adminClient
        .from('audit_reports')
        .select('id,report_type,user_id,submission_id,json_data')
        .ilike('id', `${shortId}%`)
        .limit(1);
      if (!error && data?.length > 0) { cache[shortId] = data[0]; return data[0]; }
    } catch { }
    return null;
  }, [adminClient]);

  const fetchHtmlFilesWithReports = useCallback(async (freshReports, retryCount = 0) => {
    if (!adminClient) return [];
    try {
      const { data: years, error: yearsErr } = await adminClient
        .storage.from(STORAGE_BUCKET).list('', { limit: 100 });

      if (yearsErr) {
        if (retryCount < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, RETRY_DELAYS[retryCount] || 5000));
          return fetchHtmlFilesWithReports(freshReports, retryCount + 1);
        }
        return [];
      }

      const cache = {};
      (freshReports || []).forEach(r => { if (r?.id) cache[r.id.substring(0, 8)] = r; });

      let all = [];
      for (const year of (years || []).filter(y => y.id === null)) {
        const { data: months } = await adminClient.storage.from(STORAGE_BUCKET).list(year.name, { limit: 100 });
        for (const month of (months || []).filter(m => m.id === null)) {
          const monthPath = `${year.name}/${month.name}`;
          const { data: files } = await adminClient.storage.from(STORAGE_BUCKET).list(monthPath, { limit: 1000 });

          const htmlFilesBatch = await Promise.all(
            (files || []).filter(f => f.name?.endsWith('.html')).map(async (file) => {
              const fullPath = `${monthPath}/${file.name}`;
              const { data: { publicUrl } } = adminClient.storage.from(STORAGE_BUCKET).getPublicUrl(fullPath);

              const shortMatch = file.name.match(/_([a-f0-9]{8})_RPT-/i);
              const shortId    = shortMatch?.[1] || null;
              let report       = shortId ? await lookupAuditByShortId(shortId, cache) : null;

              if (!report) {
                const rptMatch = file.name.match(/(RPT-\d+-[A-Z0-9]+)/i);
                const rptId    = rptMatch?.[1] || null;
                if (rptId) {
                  const cacheKey = `rpt-${rptId}`;
                  if (cache[cacheKey]) {
                    report = cache[cacheKey];
                  } else {
                    try {
                      const { data: rd } = await adminClient
                        .from('audit_reports')
                        .select('id,report_type,user_id,submission_id,json_data')
                        .eq('report_id', rptId)
                        .limit(1);
                      if (rd?.length > 0) {
                        report = rd[0];
                        cache[cacheKey] = report;
                        if (report.id) cache[report.id.substring(0, 8)] = report;
                      }
                    } catch { /* non-fatal */ }
                  }
                }
              }

              const fullAuditId = report?.id || null;
              const rawReportType = (report?.report_type || report?.json_data?.report_type || '').toString().toLowerCase().trim();
              const auditType = rawReportType === 'paid' ? 'paid' : 'free';
              const fileId = fullAuditId ? `audit-${fullAuditId}` : `html-${fullPath}`;
              const isAlreadyAssigned = assignedFileIdsRef.current.has(fileId);

              return {
                id:           fileId,
                name:         file.name,
                full_path:    fullPath,
                storagePath:  fullPath,
                viewUrl:      publicUrl,
                company_name: report?.company_name || file.name.split('_')[0] || 'Unknown',
                userName:     report?.user_profiles?.full_name || file.name.split('_')[0] || 'Unknown',
                userId:       report?.user_id,
                submissionId: report?.submission_id,
                generatedAt:  file.created_at || new Date().toISOString(),
                auditId:      fullAuditId,
                report_type:  rawReportType || 'free',
                auditType,
                assigned:     isAlreadyAssigned,
                status:       isAlreadyAssigned ? 'assigned' : undefined,
              };
            })
          );
          all = [...all, ...htmlFilesBatch.filter(Boolean)];
        }
      }

      all.sort((a, b) => {
        const ts = n => { const m = n.match(/_(\d+)\.html$/); return m ? parseInt(m[1]) : 0; };
        return ts(b.name) - ts(a.name);
      });

      setHtmlFiles(all);
      return all;
    } catch (err) {
      console.error('fetchHtmlFiles error:', err);
      if (retryCount < MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, RETRY_DELAYS[retryCount] || 5000));
        return fetchHtmlFilesWithReports(freshReports, retryCount + 1);
      }
      return [];
    }
  }, [adminClient, lookupAuditByShortId]);

  // ─── Normalised data ──────────────────────────────────────────────────────

  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.id] = u; });
    return map;
  }, [users]);

  const pendingAudits = useMemo(() => {
    const orderBySubmissionId = {};
    orders.forEach(o => {
      if (o.submission_id) {
        const existing = orderBySubmissionId[o.submission_id];
        const isBetter = !existing
          || o.payment_status === 'captured'
          || o.payment_status === 'received';
        if (isBetter) orderBySubmissionId[o.submission_id] = o;
      }
    });

    const reportByRequestId = {};
    auditReports.forEach(r => {
      const key = r.deck_submissions?.request_id;
      if (key) reportByRequestId[key] = r;
    });

    const publishedSubmissionIds = new Set(
      auditReports
        .filter(r => r.status === 'published' && r.published_at && r.submission_id)
        .map(r => r.submission_id)
    );

    return submissions
      .filter(s => {
        const status = s.status?.toLowerCase();
        if (['completed', 'published'].includes(status)) return false;
        if (publishedSubmissionIds.has(s.id)) return false;
        return ['pending', 'processing', 'in_review'].includes(status);
      })
      .map(sub => {
        const user = userMap[sub.user_id] || {};
        const report = reportByRequestId[sub.request_id] || null;

        const auditType      = sub.audit_type || 'free';
        const isActuallyPaid = auditType === 'paid';
        const directOrder    = isActuallyPaid ? orderBySubmissionId[sub.id] : null;

        const isDirectlyLinked      = !!(report && report.submission_id === sub.id);
        const linkedHtmlUrl         = isDirectlyLinked ? (report.html_url          || null) : null;
        const linkedHtmlContent     = isDirectlyLinked ? (report.html_content      || null) : null;
        const linkedHtmlStoragePath = isDirectlyLinked ? (report.html_storage_path || null) : null;

        return {
          id: sub.id,
          _sourceId:    sub.id,
          _sourceTable: 'deck_submissions',
          _hasReport:   isDirectlyLinked && !!linkedHtmlUrl,
          _reportId:    report?.id || null,

          userName:      user.full_name || user.email || 'Unknown User',
          userEmail:     user.email || 'No email',
          userId:        sub.user_id,
          userRole:      user.role || 'user',
          userAvatar:    DEFAULT_AVATAR,
          userAvatarAlt: `Profile picture of ${user.full_name || user.email || 'user'}`,

          auditType,
          requestId:         sub.request_id || `SUB-${sub.id.substring(0, 8)}`,
          presentationTitle: sub.title || 'Untitled Submission',
          fileType:          sub.file_name?.split('.').pop()?.toUpperCase() || 'PDF',
          slideCount:        sub.slide_count || 0,
          fileName:          sub.file_name,
          deckFileName:      sub.file_name,
          fileUrl:           sub.file_url,

          submissionDate: sub.submitted_at,

          // ✅ FIX 1: registrationDate logic per audit type + payment status
          //
          // Free audit  → created_at  (form submitted = request date)
          //
          // Paid audit:
          //   captured  → paid_at       (payment confirmed time) ✅
          //   failed    → order created_at (when payment was attempted) ✅
          //   cancelled → order created_at (when order was raised) ✅
          //   no order  → sub.submitted_at (fallback)
          registrationDate: isActuallyPaid
            ? (
                directOrder?.paid_at      ||  // captured → exact payment time
                directOrder?.created_at   ||  // failed/cancelled → order attempt time
                sub.submitted_at          ||  // fallback
                sub.created_at                // last resort
              )
            : sub.created_at,

          status:   sub.status,
          industry: sub.industry,
          purpose:  sub.purpose,
          audience: sub.audience,
          notes:    sub.notes,

          htmlStoragePath: linkedHtmlStoragePath,
          htmlContent:     linkedHtmlContent,
          htmlUrl:         linkedHtmlUrl,
          companyName:     report?.company_name || null,
          totalScore:      report?.total_score  || null,

          paymentStatus:     isActuallyPaid ? (directOrder?.payment_status      || report?.paymentStatus     || null) : null,
          razorpayPaymentId: isActuallyPaid ? (directOrder?.razorpay_payment_id || report?.razorpayPaymentId || null) : null,
        };
      });
  }, [submissions, auditReports, userMap, orders]);

  const completedAudits = useMemo(() => {
    return auditReports
      .filter(r =>
        r.status === 'published' &&
        r.published_at &&
        r.id &&
        !r.id.startsWith('pending-') &&
        !r.id.startsWith('user-')
      )
      .map(report => {
        const user = report.user_profiles || {};
        let auditType = 'free';
        if (
          report.report_type?.toLowerCase() === 'paid' ||
          report.json_data?.report_type?.toLowerCase() === 'paid'
        ) auditType = 'paid';

        return {
          id: report.id,
          _sourceId:    report.id,
          _sourceTable: 'audit_reports',
          _hasReport:   true,
          _reportId:    report.id,

          userName:      user.full_name || user.email || 'Unknown User',
          userEmail:     user.email || 'No email',
          userId:        report.user_id,
          userRole:      user.role || 'user',
          userAvatar:    DEFAULT_AVATAR,
          userAvatarAlt: `Profile picture of ${user.full_name || user.email || 'user'}`,

          auditType,
          requestId:         report.deck_submissions?.request_id || null,
          presentationTitle: report.deck_submissions?.title || report.company_name || report.deck_name || 'Untitled Audit',
          fileType:          'HTML',
          slideCount:        report.total_slides || report.deck_submissions?.slide_count || 0,
          fileName:          report.html_storage_path?.split('/').pop() || report.file_name,
          deckFileName:      report.deck_submissions?.file_name || null,
          fileUrl:           report.deck_submissions?.file_url || null,

          submissionDate:   report.uploaded_at,
          registrationDate: user.created_at,
          publishedDate:    report.published_at,
          status:           report.status || 'published',

          htmlStoragePath: report.html_storage_path,
          htmlContent:     report.html_content,
          htmlUrl:         report.html_url || null,
          companyName:     report.company_name,
          totalScore:      report.total_score,

          paymentStatus:     report?.paymentStatus     || null,
          razorpayPaymentId: report?.razorpayPaymentId || null,
        };
      });
  }, [auditReports]);

  const transformedUsers = useMemo(() => {
    const map = new Map();
    users.forEach(user => {
      const userSubs     = submissions.filter(s => s.user_id === user.id);
      const pendingCount = userSubs.filter(s => ['pending', 'processing', 'in_review'].includes(s.status)).length;

      if (userSubs.length > 0) {
        userSubs.forEach(sub => {
          const key = `${user.id}-${sub.id}`;
          if (!map.has(key)) {
            map.set(key, {
              id:           user.id,
              submissionId: sub.id,
              requestId:    sub.request_id,
              name:         user.full_name || user.email,
              email:        user.email,
              auditType:    sub.audit_type || 'free',
              pendingSubmissions: pendingCount,
            });
          }
        });
      } else {
        const key = `${user.id}-no-sub`;
        if (!map.has(key)) {
          map.set(key, {
            id: user.id, submissionId: null, requestId: null,
            name:      user.full_name || user.email,
            email:     user.email,
            auditType: 'free',
            pendingSubmissions: 0,
          });
        }
      }
    });
    return Array.from(map.values());
  }, [users, submissions]);

  const filteredHtmlFiles = useMemo(() => {
    return htmlFiles.map(f => ({
      ...f,
      status:   assignedFileIdsRef.current.has(f.id) ? 'assigned' : (f.status || undefined),
      assigned: assignedFileIdsRef.current.has(f.id) || f.assigned,
    }));
  }, [htmlFiles, assignedFileIds]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleViewDetails = useCallback((audit) => {
    if (audit.htmlUrl || audit._hasReport) {
      const htmlFile = htmlFiles.find(f =>
        f.auditId === audit._reportId ||
        f.id === `audit-${audit._reportId}`
      );
      // console.log(audit);
      setPreviewFile({
        id:           audit._reportId || audit.id,
        auditId:      audit._reportId || audit.id,
        name:         audit.fileName || `${audit.presentationTitle}.html`,
        userName:     audit.userName,
        htmlContent:  audit.htmlContent,
        full_path:    audit.htmlStoragePath || htmlFile?.full_path,
        storagePath:  audit.htmlStoragePath || htmlFile?.storagePath,
        viewUrl:      audit.htmlUrl || htmlFile?.viewUrl || null,
        company_name: audit.companyName,
        auditType:    audit.auditType,
        userEmail:    audit.userEmail,
        status:       audit.status,
      });
    } else {
      setDetailAudit(audit);
    }
  }, [htmlFiles]);

  const handleFileUpload = useCallback((files) => {
    toast.success(`${files.length} file${files.length > 1 ? 's' : ''} queued — generating HTML…`, { duration: 4000 });
    [3000, 6000, 9000].forEach((delay, i) => {
      const id = setTimeout(async () => {
        await fetchHtmlFilesWithReports(auditReports);
      }, delay);
      refreshAttemptsRef.current[`attempt-${i}`] = id;
    });
  }, [auditReports, fetchHtmlFilesWithReports]);

  const handleAssignComplete = useCallback(async (assignments) => {
    try {
      const invalid = assignments.filter(a => !a.auditId || a.auditId.startsWith('html-'));
      if (invalid.length > 0) {
        toast.error('Assignment failed: invalid audit IDs. Please refresh and try again.');
        return;
      }

      await Promise.all(assignments.map(a =>
        auditReportService.assignAuditToUser({
          auditId:      a.auditId,
          userId:       a.userId,
          submissionId: a.submissionId,
          assignedAt:   a.assignedAt,
          auditType:    a.auditType,
        })
      ));

      const ids = assignments.map(a => a.fileId);
      updateAssignedIds(prev => {
        const n = new Set(prev);
        ids.forEach(id => n.add(id));
        return n;
      });

      const msg = `${assignments.length} audit${assignments.length > 1 ? 's' : ''} assigned — ready for review`;
      setSuccessMessage(msg);
      toast.success(msg, { icon: '🔍', duration: 5000 });
      setTimeout(() => setSuccessMessage(''), 5000);

      fetchData().catch(console.error);
    } catch (err) {
      console.error('handleAssignComplete error:', err);
      toast.error('Error assigning audits. Please try again.');
    }
  }, [fetchData, updateAssignedIds]);

  const handlePublish = useCallback(async (file) => {
    // console.log('handlePublish', file);
    
    try {
      await auditReportService.publishAuditReport(file.auditId || file.id);
      // Send report published email after successful publish
      await emailService.sendReportPublishedEmail(
        file.userEmail,
        file.userName,
        file.auditType,
        file.auditId || file.id
      );
      toast.success(`Audit published to ${file.userName}`, { icon: '🚀' });
      setPreviewFile(null);
      await fetchData();
    } catch (err) {
      console.error('handlePublish error:', err);
      toast.error('Error publishing audit.');
    }
  }, [fetchData]);

  const handleDeleteHtmlFile = useCallback(async (fileId) => {
    if (!adminClient) { toast.error('Admin client not available'); return; }
    try {
      const file = htmlFiles.find(f => f.id === fileId);
      if (!file) throw new Error('File not found');
      if (file.storagePath) {
        const { error } = await adminClient.storage.from(STORAGE_BUCKET).remove([file.storagePath]);
        if (error) throw new Error(error.message);
      }
      setHtmlFiles(prev => prev.filter(f => f.id !== fileId));
      updateAssignedIds(prev => { const n = new Set(prev); n.delete(fileId); return n; });
      toast.success('File deleted', { icon: '🗑️' });
    } catch (err) {
      toast.error(err.message);
    }
  }, [adminClient, htmlFiles, updateAssignedIds]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    toast.success('Refreshed');
  }, [fetchData]);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading && !isRefreshing) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader userRole="admin" pendingAudits={0} />
        <main className="pt-[64px]">
          <div className="flex items-center justify-center py-24">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  const unassignedCount = htmlFiles.filter(f => !assignedFileIdsRef.current.has(f.id)).length;

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        <RoleBasedHeader userRole="admin" pendingAudits={pendingAudits.length} />

        <main className="pt-[64px]">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-6 md:space-y-8">

            {/* Page header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="space-y-1">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">Audit Management</h1>
                <p className="text-sm md:text-base text-muted-foreground">Process and deliver DecX Score audits to users</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" iconName="RefreshCw" onClick={handleRefresh} loading={isRefreshing}>
                  Refresh
                </Button>
                <AdminQueueIndicator queueCount={pendingAudits.length} estimatedTime={pendingAudits.length * 15} />
                <StatusNotificationBadge count={unassignedCount} type="success" label="Ready to assign" />
                {assignedFileIds.size > 0 && (
                  <StatusNotificationBadge count={assignedFileIds.size} type="info" label="Assigned" />
                )}
              </div>
            </div>

            {/* Alerts */}
            {successMessage && (
              <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                <p className="text-sm font-medium text-success flex-1">{successMessage}</p>
                <Button variant="ghost" size="icon" onClick={() => setSuccessMessage('')}><Icon name="X" size={18} /></Button>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <p className="text-sm font-medium text-destructive flex-1">{error}</p>
                <Button variant="ghost" size="icon" onClick={() => setError(null)}><Icon name="X" size={18} /></Button>
              </div>
            )}

            {/* Pending Submissions */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">Pending Submissions</h2>
                <span className="text-sm text-muted-foreground">{pendingAudits.length} total</span>
              </div>
              <PendingAuditsTable
                audits={pendingAudits}
                onViewDetails={handleViewDetails}
                onAuditDeleted={fetchData}
              />
            </section>

            {/* Completed Submissions */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">Completed Submissions</h2>
                <span className="text-sm text-muted-foreground">{completedAudits.length} total</span>
              </div>
              <CompletedSubmissionsTable
                audits={completedAudits}
                onViewDetails={handleViewDetails}
              />
            </section>

            {/* Upload Interface */}
            <AuditUploadInterface onFileUpload={handleFileUpload} />

            {/* User Assignment Panel */}
            <UserAssignmentPanel
              htmlFiles={filteredHtmlFiles}
              users={transformedUsers}
              onAssignComplete={handleAssignComplete}
              onAssignmentSuccess={(ids) => {
                updateAssignedIds(prev => {
                  const n = new Set(prev);
                  ids.forEach(id => n.add(id));
                  return n;
                });
              }}
              onDeleteFile={handleDeleteHtmlFile}
              supabaseClient={adminClient}
            />

          </div>
        </main>

        <FilePreviewModal
          file={previewFile}
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          onPublish={handlePublish}
        />

        <SubmissionDetailModal
          audit={detailAudit}
          isOpen={!!detailAudit}
          onClose={() => setDetailAudit(null)}
        />

        <GlobalFooter />
      </div>
    </ErrorBoundary>
  );
};

// Fade-in animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
  .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
`;
if (!document.getElementById('admin-audit-styles')) {
  style.id = 'admin-audit-styles';
  document.head.appendChild(style);
}

export default AdminAuditManagement;